/**
 * Unrouted captures, at the top of the task list (SPEC.md §0, §8.2).
 *
 * §0: "Anything the parser can't confidently route goes to a visible inbox. Nothing is
 * dropped, nothing is filed somewhere plausible-looking."
 *
 * Visible is the operative word, so this sits at the top of the list you already read
 * rather than behind a counter you have to go and find. Each item expands into the same
 * field set as adding a task, because resolving one *is* filing it — it just starts from
 * text you already wrote down.
 *
 * `standalone` renders it as a card of its own rather than a group inside the task list.
 * The multi-column layout can put it in a different column from the tasks, at which point
 * "at the top of the list you already read" is satisfied by it being its own card — but the
 * group chrome, a nested border on a tinted background, would then be a box inside a box.
 */

import { useState } from "react";
import type {
  Area,
  CaptureTier,
  InboxAiRetryResult,
  InboxItem,
  InboxRefineBody,
  Project,
  RecurrenceRequest,
} from "../api";
import { DestinationPicker, DetailFields, EnergyPicker, FlagButtons } from "./TaskFields";
import { EMPTY_FIELDS, type TaskFieldValues } from "./taskFieldValues";
import { buildRRuleString, todayLocalDate } from "./recurrence";
import { NoteForm } from "./NoteForm";
import {
  EMPTY_NOTE_FIELDS,
  destinationToIds,
  noteAiTitle,
  type NoteFieldValues,
} from "./noteFieldValues";
import { SplitButton } from "./SplitButton";
import { Clock, MapPin } from "./icons";
import { useBulbTier } from "../bulbTier";
import { openFiled, useEditPopups } from "../editPopupContext";
import { CaptureTierPicker } from "./CaptureTierPicker";
import { scopeOf } from "../contextScope";
import { InboxAiRetryButton } from "./InboxAiRetryButton";
import { ContextScopePicker } from "./ContextScopePicker";
import { InboxQueueRow } from "./InboxQueueRow";
import { captureTimeLabel, mapsHref } from "./taskFormat";

export type ResolveRequest = {
  action: "task" | "area" | "project" | "note" | "context" | "discard";
  title?: string;
  area_id?: string;
  project_id?: string;
  energy?: number | null;
  important?: boolean;
  starred?: boolean;
  due?: string | null;
  estimate_min?: number | null;
  notes?: string | null;
};

/** Which form is open in place of the collapsed row, if any. */
/** The bulb a pending refine wears while it runs: the form it was pressed on. */
const REFINE_SURFACE = {
  task: "inbox-refine",
  note: "note-form",
  project: "project-form",
  context: "context-form",
} as const;

export type Mode = "task" | "area" | "project" | "note" | "context" | null;

export function InboxSection({
  items,
  areas,
  projects,
  onResolve,
  onRetryAi,
  onRefineAi,
  onRetryAiSettled,
  onAwaitResult,
  pinned,
  watching,
  standalone = false,
}: {
  items: InboxItem[];
  areas: Area[];
  projects: Project[];
  /**
   * `recurrence` is only meaningful for `action: "task"` — see `TaskEditor`'s `onSave` for
   * why it travels separately from the request instead of being one more field on it.
   */
  onResolve: (
    item: InboxItem,
    request: ResolveRequest,
    recurrence?: RecurrenceRequest,
  ) => Promise<void>;
  /** Runs any not-yet-processed item through the model now and resolves with what it made of
   * it. Successful-but-uncertain parses stay a human filing decision rather than being
   * needlessly sent to the model again. */
  onRetryAi: (item: InboxItem, tier: CaptureTier | null) => Promise<InboxAiRetryResult>;
  /** Hands the item to the AI with what the Create-task form held (#82). Unlike a retry it
   * resolves the item whatever the AI says, so the answer is cleared by the row leaving. */
  onRefineAi: (
    item: InboxItem,
    request: InboxRefineBody,
    recurrence?: RecurrenceRequest,
  ) => Promise<InboxAiRetryResult>;
  /** The retry's answer has been read and cleared away — time to refresh. Deliberately a
   * separate step: refreshing the moment the model answers would drop a filed item from the
   * list and take its "Created Task: …" message with it. */
  onRetryAiSettled: (item: InboxItem, result: InboxAiRetryResult | null) => void | Promise<void>;
  /** Resolves with the worker's answer for a queued item it is working on (or just finished). */
  onAwaitResult: (item: InboxItem) => Promise<InboxAiRetryResult>;
  /** Items whose row must stay in the section it started the AI's run in, so the answer being
   * shown is not unmounted when the item's own state moves it to the other one. */
  pinned?: ReadonlyMap<string, "queue" | "unresolved">;
  /** Queue items to follow in the bulb: the worker has them, or has just finished with them. */
  watching?: ReadonlySet<string>;
  standalone?: boolean;
}) {
  if (items.length === 0) return null;

  const inQueue = (item: InboxItem) =>
    (pinned?.get(item.id) ?? (item.state === "queued" || item.state === "processing" ? "queue" : "unresolved")) ===
    "queue";
  const queue = items.filter(inQueue);
  const unresolved = items.filter((item) => !inQueue(item));
  return (
    <section className={standalone ? "panel" : "group group-inbox"} data-testid="group-inbox">
      {standalone ? (
        <header>
          <h2>Inbox · {items.length}</h2>
          <span className="muted small">Captured, not yet filed</span>
        </header>
      ) : (
        <div className="group-head">
          <h3>Inbox · {items.length}</h3>
          <span className="muted small">Captured, not yet filed</span>
        </div>
      )}
      {/* The Queue is only here while something is in it: the Add box's entries wait in Just added
          now (D224), so what lands in this card is what a person has to deal with. */}
      {queue.length > 0 && (
        <p className="inbox-subhead" data-testid="inbox-queue-head">
          <strong>Queue · {queue.length}</strong>
          <span className="muted small">The AI is on these</span>
        </p>
      )}
      {queue.length > 0 && (
        <ul className="tasks" data-testid="inbox-queue">
          {queue.map((item) => (
            <InboxQueueRow
              key={item.id}
              item={item}
              watching={watching?.has(item.id) ?? false}
              onRunNow={onRetryAi}
              onAwaitResult={onAwaitResult}
              onSettled={onRetryAiSettled}
            />
          ))}
        </ul>
      )}
      <p className="inbox-subhead" data-testid="inbox-unresolved-head">
        <strong>Unresolved · {unresolved.length}</strong>
        <span className="muted small">
          {unresolved.length > 0 ? "Waiting on you" : "Nothing waiting on you"}
        </span>
      </p>
      {unresolved.length > 0 && (
        <ul className="tasks">
          {unresolved.map((item) => (
            <InboxRow
              key={item.id}
              item={item}
              areas={areas}
              projects={projects}
              onResolve={onResolve}
              onRetryAi={onRetryAi}
              onRefineAi={onRefineAi}
              onRetryAiSettled={onRetryAiSettled}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

/** One unresolved item and the forms that file it. Also the row of a line in Just added (D224),
 * `embedded`: the bin was chosen on the line's pill, so `initialMode` opens its form and the
 * "Create as" menu and Dismiss are not offered (there is one way to choose, and a line is sent to
 * the Inbox or filed, not dismissed); `onClose` is told when the form is cancelled, and the bulb
 * hands its answer straight back (`instant`) for the line to redraw as whatever it became. */
export function InboxRow({
  item,
  areas,
  projects,
  onResolve,
  onRetryAi,
  onRefineAi,
  onRetryAiSettled,
  initialMode = null,
  embedded = false,
  onClose,
}: {
  initialMode?: Mode;
  embedded?: boolean;
  onClose?: () => void;
  item: InboxItem;
  areas: Area[];
  projects: Project[];
  onResolve: (
    item: InboxItem,
    request: ResolveRequest,
    recurrence?: RecurrenceRequest,
  ) => Promise<void>;
  onRetryAi: (item: InboxItem, tier: CaptureTier | null) => Promise<InboxAiRetryResult>;
  onRefineAi: (
    item: InboxItem,
    request: InboxRefineBody,
    recurrence?: RecurrenceRequest,
  ) => Promise<InboxAiRetryResult>;
  onRetryAiSettled: (item: InboxItem, result: InboxAiRetryResult | null) => void | Promise<void>;
}) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [busy, setBusy] = useState(false);
  // The form's values, stashed when its lightbulb is pressed: the form closes back to the idle
  // row, whose AI button starts straight away with the refine call in place of a retry.
  const [refining, setRefining] = useState<{
    request: InboxRefineBody;
    recurrence?: RecurrenceRequest;
  } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const { tier: refineTier } = useBulbTier("inbox-refine");
  const { tier: noteTier } = useBulbTier("note-form");
  const { tier: projectTier } = useBulbTier("project-form");
  const { tier: contextTier } = useBulbTier("context-form");
  const popups = useEditPopups();

  // Task
  const [title, setTitle] = useState(item.raw_text);
  const [values, setValues] = useState<TaskFieldValues>({ ...EMPTY_FIELDS });
  const update = (patch: Partial<TaskFieldValues>) =>
    setValues((current) => ({ ...current, ...patch }));

  // Area
  const [areaName, setAreaName] = useState(item.raw_text);

  // Project
  const [projectName, setProjectName] = useState(item.raw_text);
  const [projectAreaId, setProjectAreaId] = useState("");

  // Context (D222) — a title, the text, and which AI requests it is read into.
  const [contextTitle, setContextTitle] = useState(item.raw_text);
  const [contextBody, setContextBody] = useState(item.cleaned_text ?? item.raw_text);
  const [contextScope, setContextScope] = useState("");

  // Note — no destination required (§0's filing decision is exactly what a note skips).
  const [noteValues, setNoteValues] = useState<NoteFieldValues>({
    ...EMPTY_NOTE_FIELDS,
    title: item.raw_text,
  });
  const updateNote = (patch: Partial<NoteFieldValues>) =>
    setNoteValues((current) => ({ ...current, ...patch }));

  /** The form's fields as `POST /api/tasks` knows them — shared by "Create task" and the
   * lightbulb, which must read the same form the same way. Only valid with a destination for
   * the former; the latter sends none when none was picked. */
  function taskFields(): InboxRefineBody {
    return {
      title: title.trim() || item.raw_text,
      ...(values.destination.startsWith("project:")
        ? { project_id: values.destination.slice(8) }
        : values.destination.startsWith("area:")
          ? { area_id: values.destination.slice(5) }
          : {}),
      energy: values.energy,
      important: values.important,
      starred: values.starred,
      due: values.due || null,
      estimate_min: values.estimate.trim() ? Number(values.estimate) : null,
      notes: values.notes || null,
    };
  }

  function taskRecurrence(): RecurrenceRequest | undefined {
    return values.recurrence.enabled
      ? {
          rrule: buildRRuleString(values.recurrence),
          recur_anchor: values.recurrence.anchor,
          recur_start: values.due || todayLocalDate(),
        }
      : undefined;
  }

  async function fileTask(event: React.FormEvent) {
    event.preventDefault();
    if (!values.destination) return;
    setBusy(true);
    try {
      await onResolve(item, { action: "task", ...taskFields() }, taskRecurrence());
    } finally {
      setBusy(false);
    }
  }

  function refineWithAi() {
    if (!values.destination && values.recurrence.enabled) {
      // Same refusal as the Add card, for the same reason: a repeat is set on a task, and
      // with no destination there is no task yet — only an Inbox item for the AI to route.
      setFormError(
        "Repeat needs a task to attach to. Choose an area or project first, or ask the AI " +
          "without Repeat for now.",
      );
      return;
    }
    setFormError(null);
    setRefining({
      request: { ...taskFields(), ...(refineTier ? { tier: refineTier } : {}) },
      recurrence: taskRecurrence(),
    });
    setMode(null);
  }

  /** The Save-note form's bulb: the same hand-over as the Create-task one, with the form's note
   * values. The AI makes the note, so the typed words are not put back into it. */
  function refineNoteWithAi() {
    setFormError(null);
    setRefining({
      request: {
        kind: "note",
        title: noteAiTitle(noteValues),
        notes: noteValues.body || null,
        ...destinationToIds(noteValues.destination),
        ...(noteTier ? { tier: noteTier } : {}),
      },
    });
    setMode(null);
  }

  /** The Create-project and Save-context forms' bulbs: the typed values go to the AI as the chip's. */
  function refineProjectWithAi() {
    setFormError(null);
    setRefining({
      request: {
        kind: "project",
        title: projectName.trim() || item.raw_text,
        ...(projectAreaId ? { area_id: projectAreaId } : {}),
        ...(projectTier ? { tier: projectTier } : {}),
      },
    });
    setMode(null);
  }

  function refineContextWithAi() {
    setFormError(null);
    setRefining({
      request: {
        kind: "context",
        title: contextTitle.trim() || item.raw_text,
        notes: contextBody.trim() || null,
        ...scopeOf(contextScope),
        ...(contextTier ? { tier: contextTier } : {}),
      },
    });
    setMode(null);
  }

  async function fileArea(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await onResolve(item, { action: "area", title: areaName.trim() || item.raw_text });
    } finally {
      setBusy(false);
    }
  }

  async function fileProject(event: React.FormEvent) {
    event.preventDefault();
    if (!projectAreaId) return;
    setBusy(true);
    try {
      await onResolve(item, {
        action: "project",
        title: projectName.trim() || item.raw_text,
        area_id: projectAreaId,
      });
    } finally {
      setBusy(false);
    }
  }

  async function fileNote() {
    setBusy(true);
    try {
      await onResolve(item, {
        action: "note",
        title: noteValues.title.trim() || item.raw_text,
        notes: noteValues.body || null,
        ...destinationToIds(noteValues.destination),
      });
    } finally {
      setBusy(false);
    }
  }

  async function fileContext(event: React.FormEvent) {
    event.preventDefault();
    if (!contextBody.trim()) return;
    setBusy(true);
    try {
      await onResolve(item, {
        action: "context",
        title: contextTitle.trim() || item.raw_text,
        notes: contextBody,
        ...destinationToIds(contextScope),
      });
    } finally {
      setBusy(false);
    }
  }

  async function discard() {
    if (
      !window.confirm(
        `Dismiss "${item.raw_text}"?\n\n` +
          "No task is created. The item is kept and marked resolved, so you can still see " +
          "it under resolved items - it is not deleted.",
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await onResolve(item, { action: "discard" });
    } finally {
      setBusy(false);
    }
  }

  /** Cancel on a form: back to the idle row, and tell whoever opened it from outside. */
  function cancelForm() {
    setFormError(null);
    setMode(null);
    onClose?.();
  }

  if (mode === null) {
    return (
      <li className="task inbox-row" data-testid="inbox-item">
        <span className="title">
          <span className="text">{item.raw_text}</span>
          <span className="meta">
            <span className="badge">{item.source}</span>
            <span className="badge" title="When this was captured">
              <Clock size={12} />
              {captureTimeLabel(item.created_at, item.client_context?.captured_at)}
            </span>
            {item.client_context?.lat != null && item.client_context?.lon != null && (
              <a
                className="badge location"
                href={mapsHref(item.client_context.lat, item.client_context.lon)}
                title={`${item.client_context.lat}, ${item.client_context.lon} - open in Maps`}
                aria-label="Open capture location in Maps"
              >
                <MapPin size={12} />
              </a>
            )}
            {item.reason && <span className="muted">{item.reason}</span>}
          </span>
        </span>
        <span className="task-actions">
          {/* Every item still in the Inbox can be put to the model — including one it already
              answered but wasn't sure about. Hiding the bulb on those (as this first did) left
              an item that was plainly still here with no way to ask again, e.g. on a higher tier. */}
          <InboxAiRetryButton
            // While a refine runs, the button wears the form bulb's tier, not the retry bulb's.
            surface={
              refining ? (REFINE_SURFACE[refining.request.kind ?? "task"]) : "inbox-retry"
            }
            onRetry={(tier) =>
              refining
                ? onRefineAi(item, refining.request, refining.recurrence)
                : onRetryAi(item, tier)
            }
            onSettled={async (result) => {
              await onRetryAiSettled(item, result);
              setRefining(null);
            }}
            autoStart={refining !== null}
            leaveOnClear={refining !== null}
            instant={embedded}
            onEdit={(created) => openFiled(popups, created.kind, created.id)}
          />
          {/* `inbox-fold` on this and Dismiss: while an AI retry is showing its answer they fold
              away to give it the row (see `inboxAiRetryMotion.ts`). */}
          {!embedded && (
            <SplitButton
              className="inbox-fold"
              label="Create task"
              onClick={() => setMode("task")}
              items={[
                { key: "task", label: "Task", onSelect: () => setMode("task") },
                { key: "area", label: "Area", onSelect: () => setMode("area") },
                { key: "project", label: "Project", onSelect: () => setMode("project") },
                { key: "note", label: "Note", onSelect: () => setMode("note") },
                { key: "context", label: "Context", onSelect: () => setMode("context") },
              ]}
            />
          )}
          {!embedded && (
            <button
              className="btn ghost small inbox-fold"
              onClick={() => void discard()}
              disabled={busy}
              title="Not going to become anything. The record is kept; it just resolves to nothing."
            >
              Dismiss
            </button>
          )}
        </span>
      </li>
    );
  }

  if (mode === "area") {
    return (
      <li className="task editing">
        <form onSubmit={fileArea} style={{ display: "grid", gap: "0.55rem", width: "100%" }}>
          <input
            className="input"
            aria-label="Area name"
            value={areaName}
            onChange={(e) => setAreaName(e.target.value)}
            autoFocus
          />

          <div className="row wrap">
            <span style={{ flex: 1 }} />
            <button type="button" className="btn ghost small" onClick={cancelForm}>
              Cancel
            </button>
            <button className="btn primary small" type="submit" disabled={busy}>
              {busy ? "Creating…" : "Create area"}
            </button>
          </div>
        </form>
      </li>
    );
  }

  if (mode === "project") {
    return (
      <li className="task editing">
        <form onSubmit={fileProject} style={{ display: "grid", gap: "0.55rem", width: "100%" }}>
          <input
            className="input"
            aria-label="Project name"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            autoFocus
          />

          <select
            className="input"
            style={{ maxWidth: "16rem" }}
            aria-label="Area"
            value={projectAreaId}
            onChange={(e) => setProjectAreaId(e.target.value)}
          >
            <option value="">choose an area…</option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>

          {!projectAreaId && (
            <p className="small muted" style={{ margin: 0 }}>
              Pick an area - a project needs one to belong to.
            </p>
          )}

          <div className="row wrap">
            <span style={{ flex: 1 }} />
            <button type="button" className="btn ghost small" onClick={cancelForm}>
              Cancel
            </button>
            <CaptureTierPicker surface="project-form" onPress={refineProjectWithAi} />
            <button
              className="btn primary small"
              type="submit"
              disabled={busy || !projectAreaId}
            >
              {busy ? "Creating…" : "Create project"}
            </button>
          </div>
        </form>
      </li>
    );
  }

  if (mode === "context") {
    return (
      <li className="task editing">
        <form onSubmit={fileContext} style={{ display: "grid", gap: "0.55rem", width: "100%" }}>
          <input
            className="input"
            aria-label="Context title"
            value={contextTitle}
            onChange={(e) => setContextTitle(e.target.value)}
            autoFocus
          />
          <textarea
            className="input"
            aria-label="Context text"
            rows={3}
            value={contextBody}
            onChange={(e) => setContextBody(e.target.value)}
          />
          <div className="row wrap">
            <ContextScopePicker
              value={contextScope}
              areas={areas}
              projects={projects}
              onChange={setContextScope}
            />
          </div>
          <p className="small muted" style={{ margin: 0 }}>
            Context is background the AI reads every time it works for you. It goes only into
            your own requests, to whichever models are set up for them: a remote model sees it
            too.
          </p>
          <div className="row wrap">
            <span style={{ flex: 1 }} />
            <button type="button" className="btn ghost small" onClick={cancelForm}>
              Cancel
            </button>
            <CaptureTierPicker
              surface="context-form"
              onPress={refineContextWithAi}
              pressDisabled={!contextBody.trim()}
            />
            <button
              className="btn primary small"
              type="submit"
              disabled={busy || !contextBody.trim()}
            >
              {busy ? "Saving…" : "Save context"}
            </button>
          </div>
        </form>
      </li>
    );
  }

  if (mode === "note") {
    return (
      <li className="task editing">
        <NoteForm
          values={noteValues}
          onChange={updateNote}
          areas={areas}
          projects={projects}
          busy={busy}
          requireTitle={false}
          submitLabel="Save note"
          onSubmit={fileNote}
          onCancel={cancelForm}
          onAi={refineNoteWithAi}
        />
      </li>
    );
  }

  return (
    <li className="task editing">
      <form onSubmit={fileTask} style={{ display: "grid", gap: "0.55rem", width: "100%" }}>
        <input
          className="input"
          aria-label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
        />

        <div className="row wrap">
          <DestinationPicker
            value={values.destination}
            areas={areas}
            projects={projects}
            onChange={(destination) => update({ destination })}
            allowInbox
          />
          <EnergyPicker value={values.energy} onChange={(energy) => update({ energy })} />
          <FlagButtons
            important={values.important}
            starred={values.starred}
            onImportant={(important) => update({ important })}
            onStarred={(starred) => update({ starred })}
          />
        </div>

        <DetailFields values={values} onChange={update} />

        {!values.destination && (
          <p className="small muted" style={{ margin: 0 }}>
            Pick an area or project - that is the decision this item has been waiting on. Or
            ask the AI: the lightbulb works without one.
          </p>
        )}
        {formError && (
          <p className="error small" style={{ margin: 0 }} role="alert">
            {formError}
          </p>
        )}

        <div className="row wrap">
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="btn ghost small"
            onClick={cancelForm}
          >
            Cancel
          </button>
          <CaptureTierPicker surface="inbox-refine" onPress={refineWithAi} />
          <button
            className="btn primary small"
            type="submit"
            disabled={busy || !values.destination}
          >
            {busy ? "Creating…" : "Create task"}
          </button>
        </div>
      </form>
    </li>
  );
}
